import React, { createContext, useContext, useState, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import App from './App';
import { AppProvider } from './context/AppContext';

export const ThemeModeContext = createContext({ toggleTheme: () => {}, mode: 'dark' as 'light' | 'dark' });
export function useThemeMode() { return useContext(ThemeModeContext); }

function buildTheme(mode: 'light' | 'dark') {
  const isDark = mode === 'dark';
  const borderColor = isDark ? '#2C2C2E' : '#E5E5EA';
  const tableHeadBg = isDark ? '#1C1C1E' : '#F4F4F8';
  const paperBg = isDark ? '#1E1E1E' : '#FFFFFF';
  return createTheme({
    palette: {
      mode,
      primary: { main: '#FF3B30', light: '#FF6347', dark: '#CC2E26' },
      secondary: { main: '#00C7BE' },
      background: { default: isDark ? '#121212' : '#F2F2F7', paper: paperBg },
      text: {
        primary: isDark ? '#F5F5F7' : '#1C1C1E',
        secondary: isDark ? '#A1A1A6' : '#6E6E73',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      button: { textTransform: 'none' as const, fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: '8px', padding: '8px 16px', boxShadow: 'none' },
          containedPrimary: {
            backgroundColor: '#FF3B30',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#FF6347', boxShadow: '0 4px 12px rgba(255,59,48,0.3)' },
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none', backgroundColor: paperBg } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${borderColor}`, padding: '16px' },
          head: { fontWeight: 600, color: isDark ? '#A1A1A6' : '#6E6E73', backgroundColor: tableHeadBg },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: isDark ? '#A1A1A6' : '#C7C7CC',
            '&.Mui-checked': { color: '#00C7BE', '& + .MuiSwitch-track': { backgroundColor: '#00C7BE', opacity: 0.3 } },
          },
          track: { backgroundColor: isDark ? '#3A3A3C' : '#C7C7CC', opacity: 1 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#A1A1A6' : '#8E8E93' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C7BE' },
          },
        },
      },
      MuiAppBar: { styleOverrides: { root: { backgroundColor: paperBg, borderBottom: `1px solid ${borderColor}` } } },
      MuiDivider: { styleOverrides: { root: { borderColor } } },
      MuiDialog: { styleOverrides: { paper: { backgroundColor: paperBg, backgroundImage: 'none' } } },
      MuiMenuItem: {
        styleOverrides: {
          root: { '&.Mui-selected': { backgroundColor: isDark ? 'rgba(255,59,48,0.1)' : 'rgba(255,59,48,0.06)' } },
        },
      },
    },
  });
}

function Root() {
  const stored = (localStorage.getItem('haki_theme') as 'light' | 'dark') || 'dark';
  const [mode, setMode] = useState<'light' | 'dark'>(stored);
  const toggleTheme = () => setMode(m => { const next = m === 'dark' ? 'light' : 'dark'; localStorage.setItem('haki_theme', next); return next; });
  const theme = useMemo(() => buildTheme(mode), [mode]);
  return (
    <ThemeModeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider>
          <App />
        </AppProvider>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Root />
    </BrowserRouter>
  </React.StrictMode>
);
